import type { WamFile } from "@workadventure/map-editor";
import { DeleteTileLayerCommand, TilemapEditsManager } from "@workadventure/map-editor";
import type { GameMapFrontWrapper } from "../../../GameMap/GameMapFrontWrapper";
import type { FrontCommandInterface } from "../FrontCommandInterface";
import type { RoomConnection } from "../../../../../Connection/RoomConnection";
import { CreateTileLayerFrontCommand } from "./CreateTileLayerFrontCommand";

export class DeleteTileLayerFrontCommand extends DeleteTileLayerCommand implements FrontCommandInterface {
    /**
     * Snapshot of the layer before deletion (flat index among tile layers + unprefixed model name),
     * used to build the undo command. Undefined when the layer did not exist when the command was built.
     */
    private readonly previousState: { name: string; index: number; opacity: number; visible: boolean } | undefined;

    constructor(
        wamFile: WamFile,
        layerId: number,
        commandId: string | undefined,
        private gameMapFrontWrapper: GameMapFrontWrapper,
    ) {
        super(wamFile, layerId, commandId);
        const modelLayer = TilemapEditsManager.findTileLayer(gameMapFrontWrapper.getMap(), layerId);
        const flatLayers = gameMapFrontWrapper.getFlatLayers();
        const flatIndex = flatLayers.findIndex((flat) => flat.type === "tilelayer" && flat.id === layerId);
        if (modelLayer && flatIndex !== -1) {
            this.previousState = {
                name: modelLayer.name,
                index: flatLayers.slice(0, flatIndex).filter((flat) => flat.type === "tilelayer").length,
                opacity: modelLayer.opacity ?? 1,
                visible: modelLayer.visible ?? true,
            };
        }
    }

    public async execute(): Promise<void> {
        await super.execute();
        this.gameMapFrontWrapper.deleteTileLayerById(this.layerId);
    }

    public getUndoCommand(): CreateTileLayerFrontCommand {
        if (!this.previousState) {
            throw new Error("Cannot undo a layer deletion that was not snapshot (layer id " + this.layerId + ")");
        }
        return new CreateTileLayerFrontCommand(
            this.wamFile,
            this.layerId,
            this.previousState.name,
            this.previousState.index,
            this.previousState.opacity,
            this.previousState.visible,
            undefined,
            this.gameMapFrontWrapper,
        );
    }

    public emitEvent(roomConnection: RoomConnection): void {
        roomConnection.emitMapEditorDeleteTileLayer(this.commandId, this.layerId);
    }
}
